import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, FlexRow, TextField} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {BRAND_SWATCHES, DEFAULT_ACCENT_COLOR, normalizeHexColor} from '../config/brandSwatches';
import {FONT_OPTIONS} from '../config/fonts';
import {GroupLabel, LabelledToggle, Note, Section, Sections} from './common';
import {Select} from './Select';
import type {PanelProps} from './types';
import type {QuoteFont, QuoteTemplate} from '../resolver/types';

const PaletteCard = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
	width: 100%;
	max-width: 432px;
	padding: 24px;
	box-sizing: border-box;
	background: ${({theme}) => theme.sys.color.background.neutral['1']};
	border: 1px solid ${({theme}) => theme.sys.color.border.decorative};
	border-radius: 8px;
	box-shadow: 0 4px 16px rgba(35, 35, 52, 0.12);
`;

const PaletteHeading = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const PaletteBody = styled.div`
	width: 100%;
`;

const SwatchRow = styled.div`
	display: grid;
	grid-template-columns: repeat(7, minmax(0, 1fr));
	gap: 8px;
	margin-bottom: 8px;
`;

const SecondRow = styled.div`
	display: grid;
	grid-template-columns: repeat(7, minmax(0, 1fr));
	gap: 8px;
	align-items: center;
	margin-bottom: 12px;
`;

const SwatchGroup = styled.div`
	grid-column: span 4;
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 8px;
`;

const HexCell = styled.div`
	grid-column: span 3;
	min-width: 0;
`;

const Swatch = styled.button<{$color: string; $selected: boolean}>`
	width: 100%;
	aspect-ratio: 1;
	padding: 2px;
	border-radius: 8px;
	border: 1px solid ${({theme, $selected}) => ($selected ? theme.sys.color.brand.primary['1'] : 'transparent')};
	background: transparent;
	cursor: pointer;

	&::after {
		content: '';
		display: block;
		width: 100%;
		height: 100%;
		border-radius: 6px;
		background: ${({$color}) => $color};
	}
`;

const DefaultRow = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	padding: 14px;
	margin-top: 16px;
	border: 1px solid ${({theme}) => theme.sys.color.border.decorative};
	border-radius: 8px;
`;

/** Accent colour, typeface and logo. All three are independent of the Layout & Theme preset. */
const BrandingPanel: React.FC<PanelProps> = ({template, onChange, brandingDefault, onSaveBrandingDefault}) => {
	const intl = useIntl();
	const setBranding = (patch: Partial<QuoteTemplate['branding']>) =>
		onChange({...template, branding: {...template.branding, ...patch}});

	/*
	 * The field is edited as a draft and applied only when it spells a whole colour.
	 *
	 * Bound straight to the template it could not be typed in at all: the first keystroke left an
	 * incomplete hex, nothing was applied, and the controlled value snapped back - so every
	 * character, Backspace included, undid itself and only a one-shot paste ever worked.
	 *
	 * Six characters, not "anything normalizeHexColor accepts", because it accepts three-character
	 * shorthand too: d27 IS the colour dd2277. Applying that mid-word replaced what was being typed
	 * with the expanded form, so the next keys landed on the end of it - d279e1 typed one key at a
	 * time ended up as DD22779e1, on the wrong colour. Shorthand is still something a person can
	 * mean, so it is taken on the way out of the field rather than during.
	 */
	const accentHex = template.branding.accentColor.replace('#', '').toUpperCase();
	const [hexDraft, setHexDraft] = useState<string | null>(null);
	// Drops the draft whenever the accent moves for another reason - a swatch, Revert, the account
	// default - so the field follows the colour it is showing.
	useEffect(() => setHexDraft(null), [accentHex]);

	const applyHex = (value: string, allowShorthand: boolean) => {
		const raw = value.trim().replace(/^#/, '');
		if (!allowShorthand && raw.length !== 6) return;

		const normalized = normalizeHexColor(raw);
		if (normalized) setBranding({accentColor: normalized});
	};

	const defaultAccent = brandingDefault?.accentColor ?? DEFAULT_ACCENT_COLOR;
	const defaultFont = brandingDefault?.font ?? 'helvetica';
	const isDirty = template.branding.accentColor !== defaultAccent || template.branding.font !== defaultFont;

	const swatch = (row: 1 | 2) =>
		BRAND_SWATCHES.filter(entry => entry.row === row).map(entry => (
			<Swatch
				key={entry.value}
				type="button"
				$color={entry.value}
				$selected={template.branding.accentColor === entry.value}
				aria-label={entry.name}
				title={entry.name}
				onClick={() => setBranding({accentColor: entry.value})}
				data-cy={`quote-accent-${entry.value.slice(1)}`}
			/>
		));

	return (
		<Sections>
			<Section>
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.branding.company_logo'})}
					checked={template.branding.showLogo}
					onChange={(checked: boolean) => setBranding({showLogo: checked})}
					cy="quote-toggle-logo"
				/>
			</Section>

			<PaletteCard data-cy="quote-accent-palette">
				<PaletteHeading>
					<GroupLabel>{intl.formatMessage({id: 'quote_designer.branding.accent'})}</GroupLabel>
					<Note>{intl.formatMessage({id: 'quote_designer.branding.accent_hint'})}</Note>
				</PaletteHeading>
				<PaletteBody>
					<SwatchRow>{swatch(1)}</SwatchRow>
					<SecondRow>
						<SwatchGroup>{swatch(2)}</SwatchGroup>
						<HexCell>
							<TextField
								label={intl.formatMessage({id: 'quote_designer.branding.accent_hex'})}
								value={hexDraft ?? accentHex}
								onValueChange={(value: string) => {
									setHexDraft(value);
									applyHex(value, false);
								}}
								// Leaving the field is the one moment the value is finished, so shorthand
								// counts here; anything still unreadable falls back to the applied colour.
								onBlur={() => {
									if (hexDraft !== null) applyHex(hexDraft, true);
									setHexDraft(null);
								}}
								data-cy="quote-accent-hex"
							/>
						</HexCell>
					</SecondRow>
				</PaletteBody>
			</PaletteCard>

			<Section>
				<GroupLabel>{intl.formatMessage({id: 'quote_designer.branding.typeface'})}</GroupLabel>
				<Select
					aria-label={intl.formatMessage({id: 'quote_designer.branding.typeface'})}
					value={template.branding.font}
					onChange={event => setBranding({font: event.target.value as QuoteFont})}
					data-cy="quote-font"
				>
					{FONT_OPTIONS.map(option => (
						<option key={option.value} value={option.value}>
							{intl.formatMessage({id: option.labelId})}
						</option>
					))}
				</Select>
			</Section>

			<Section>
				<DefaultRow>
					<span>
						<div>{intl.formatMessage({id: 'quote_designer.branding.default_row'})}</div>
						<Detail color="auxiliary">
							{intl.formatMessage({
								id: isDirty ? 'quote_designer.branding.default_dirty' : 'quote_designer.branding.default_clean',
							})}
						</Detail>
					</span>
					<FlexRow gap="s" alignItems="center">
						{isDirty && (
							<Button
								emphasis={EMPHASIS.LOW}
								// Local only: this restores the account default into THIS quote and saves nothing.
								onPress={() => setBranding({accentColor: defaultAccent, font: defaultFont})}
								data-cy="quote-branding-revert"
							>
								{intl.formatMessage({id: 'quote_designer.branding.revert'})}
							</Button>
						)}
						<Button
							emphasis={EMPHASIS.MEDIUM}
							disabled={!isDirty}
							onPress={() =>
								onSaveBrandingDefault({
									accentColor: template.branding.accentColor,
									font: template.branding.font,
								})
							}
							data-cy="quote-branding-save-default"
						>
							{intl.formatMessage({id: 'quote_designer.branding.save_default'})}
						</Button>
					</FlexRow>
				</DefaultRow>
			</Section>
		</Sections>
	);
};

export default BrandingPanel;
